import { Component, Input, inject } from "@angular/core";
import { GenericListField } from "@app/shared/models/GenericList/GenericListField";
import { DashboardWidgetModel } from "../shared/models/DashboardWidgetModel";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { MatDialogConfig } from "@angular/material/dialog";
import { ICerrixPrompt } from "@app/shared/cerrix-prompt/models/ICerrixPrompt";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { take } from "rxjs";
import { translateWidgetCatalogField } from "../shared/translateWidgetCatalogField";

@Component({
    selector: "global-filtering",
    templateUrl: "./global-filtering.component.html",
    styleUrls: ["./global-filtering.component.scss"],
    providers: [provideTranslocoScope("dashboard")],
})
export class GlobalFilteringComponent {
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    @Input() widgets: DashboardWidgetModel[];
    @Input() filterValues: any = {};

    filters: GenericListField[];
    widgetsWithFilters: DashboardWidgetModel[];

    open() {
        this.resetComp();

        if (!this.filterValues) {
            this.filterValues = {};
        }

        this.widgets.forEach((widget) => {
            if (
                widget.globalFilterKeys &&
                widget.globalFilterKeys.length > 0 &&
                widget.customConfig.globalFilterEnabled
            ) {
                this.widgetsWithFilters.push(widget);
                widget.globalFilterKeys.forEach((fk) => {
                    if (!this.filters.find((f) => f.fieldName === fk)) {
                        const filterToAdd = widget.configurations.find((f) => f.fieldName === fk);
                        if (filterToAdd) {
                            // widget.configurations comes from DashboardWidgetMapping's static mapping
                            // by reference (not a deep copy), so clone before mutating/translating --
                            // otherwise this would permanently rewrite the shared static catalog.
                            const translatedFilter = this.translateField(filterToAdd);
                            translatedFilter.selfInit = true;
                            this.filters.push(translatedFilter);
                        }
                    }
                });
            }
        });

        this.showPrompt();
    }

    showPrompt() {
        const keys = [
            "dashboard.globalFiltering.title",
            "dashboard.globalFiltering.applyButton",
            "dashboard.globalFiltering.noFiltersMessage",
            "dashboard.globalFiltering.closeButton",
        ];

        this._transloco
            .selectTranslate(keys)
            .pipe(take(1))
            .subscribe(([title, applyButton, noFiltersMessage, closeButton]) => {
                this.openPrompt(title, applyButton, noFiltersMessage, closeButton);
            });
    }

    private openPrompt(
        title: string,
        applyButton: string,
        noFiltersMessage: string,
        closeButton: string
    ) {
        const promptConfig: Partial<MatDialogConfig<ICerrixPrompt>> = {
            data: {
                title: title,
                fields: this.filters,
                fieldResult: JSON.parse(JSON.stringify(this.filterValues)),
                confirmButton: {
                    text: applyButton,
                },
            },
        };

        if (this.filters.length == 0) {
            promptConfig.maxWidth = "400px";
            promptConfig.maxHeight = "250px";

            promptConfig.data.message = noFiltersMessage;
            promptConfig.data.confirmButton = { show: false };
            promptConfig.data.cancelButton = { text: closeButton };
        }

        this._promptService
            .prompt(promptConfig)
            .getResult()
            .subscribe((result) => {
                if (result) {
                    Object.getOwnPropertyNames(result).forEach((k) => {
                        this.filterValues[k] = result[k];
                    });
                    setTimeout(() => {
                        this.emitNewFilters();
                    }, 0);
                }
            });
    }

    emitNewFilters() {
        this.widgetsWithFilters.forEach((widget) => {
            if (widget.api.onGlobalFilterChange) {
                widget.api.onGlobalFilterChange();
            }
        });
    }

    resetComp() {
        this.filters = [];
        this.widgetsWithFilters = [];
    }

    // fields render through the shared generic-field-editor, which has no transloco pipe of its
    // own -- DashboardWidgetMapping's prettyName/description/dropdown Name values are
    // "dashboard.widgetCatalog.*" keys, so resolve them here first.
    private translateField(field: GenericListField): GenericListField {
        const clonedField: GenericListField = { ...field };
        translateWidgetCatalogField(this._transloco, clonedField);
        return clonedField;
    }
}
